import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown } from 'antd';
import { DotsThree } from '@phosphor-icons/react';

/**
 * The workspace page's header (design "Flow Workspace"): "Workspaces / Name" with the person's role, then Start all,
 * Stop all (both ask first: RunConfirm), Invite (opens Members) and "⋯" (Rename, Delete workspace…). Rename, Invite and
 * Delete are for the owner; Start all and Stop all act only on the projects the person may run. The buttons are small
 * default antd buttons (the theme's colors in light and dark), one quiet row on the title's baseline; the role a pill.
 */
export default function WorkspaceHeader({
  workspace, roleText, canManage, busy, hasProjects, onBack, onStart, onStop, onInvite, onRename, onDelete,
}) {
  const [asking, setAsking] = useState(null); // 'start' | 'stop' while the plan is being prepared
  const ask = async(action, handler) => {
    setAsking(action);
    try { await handler(); } finally { setAsking(null); }
  };
  const menu = {
    items: [
      { key: 'rename', label: 'Rename' },
      { key: 'delete', label: 'Delete workspace…', danger: true },
    ],
    onClick: ({ key }) => (key === 'rename' ? onRename() : onDelete()),
  };
  const working = Boolean(busy) || Boolean(asking);
  return (
    <div className="ws-head">
      <div className="ws-title">
        <button className="ws-crumb" onClick={onBack} type="button">Workspaces</button>
        <span aria-hidden="true" className="ws-crumb-sep">/</span>
        <h1>{workspace.name}</h1>
        <span className="ws-chip is-info ws-role">{roleText}</span>
      </div>
      <div className="ws-actions">
        <Button disabled={working || !hasProjects} onClick={() => ask('start', onStart)} size="small">{busy === 'start' ? 'Starting…' : 'Start all'}</Button>
        <Button disabled={working || !hasProjects} onClick={() => ask('stop', onStop)} size="small">{busy === 'stop' ? 'Stopping…' : 'Stop all'}</Button>
        {canManage && <Button onClick={onInvite} size="small">Invite</Button>}
        {canManage && (
          <Dropdown menu={menu} trigger={['click']}>
            <Button aria-label="More actions" icon={<DotsThree aria-hidden size={16} weight="bold" />} size="small" />
          </Dropdown>
        )}
      </div>
    </div>
  );
}

WorkspaceHeader.propTypes = {
  busy: PropTypes.string,
  canManage: PropTypes.bool,
  hasProjects: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onInvite: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
  roleText: PropTypes.string,
  workspace: PropTypes.object.isRequired,
};
